// Strategy Lab experiment runner: race drone deployment strategies against
// each other on identical fires.
//
// Every configuration (strategy × station density) is simulated from the
// CURRENT live fire state, against the same real weather timeline, using the
// same set of random seeds — a paired comparison, so outcome differences are
// attributable to the strategy rather than to luck. Replicates are averaged.
//
// Runs are chunked through the event loop so the UI stays responsive, and
// each config keeps a final-state snapshot for the burn-footprint minimap.

import { updateDroneMissions } from "./dronePlanner";
import { DRONE_STRATEGIES } from "./droneStrategies";

const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const yieldToUI = () => new Promise((res) => setTimeout(res, 0));

const BASE_DRONE_SPACING = 50;

export const computeStations = (rows, cols, density) => {
  const spacing = Math.max(1, Math.round(BASE_DRONE_SPACING / density));
  const offset = Math.floor(spacing / 2);
  const stations = [];
  for (let r = offset; r < rows; r += spacing) {
    for (let c = offset; c < cols; c += spacing) {
      stations.push({ row: r, col: c });
    }
  }
  return stations;
};

// configs: [{ strategyId, density }]
export const runExperiments = async ({
  engine,
  weatherTimeline,
  startMs,
  horizonHours = 48,
  replicates = 3,
  configs,
  onProgress,
}) => {
  const baseStats = engine.stats();
  const seeds = Array.from({ length: replicates }, (_, i) => 0x51ab0b ^ (i * 7919));
  const totalRuns = configs.length * replicates;
  let doneRuns = 0;
  const results = [];

  for (const cfg of configs) {
    const strategy = DRONE_STRATEGIES[cfg.strategyId];
    const stations =
      cfg.strategyId === "none"
        ? []
        : computeStations(engine.rows, engine.cols, cfg.density);
    const agg = { infraLost: 0, valueLost: 0, burntTiles: 0, drops: 0 };
    let finalState = null;
    const lossCurve = new Float64Array(horizonHours + 1);

    for (let rep = 0; rep < replicates; rep++) {
      const sim = engine.clone(mulberry32(seeds[rep]));
      let missions = [];
      let dropsUsed = 0;

      for (let s = 0; s < horizonHours; s++) {
        const t = startMs + (s + 1) * 3600000;
        const weather = weatherTimeline.at(t);

        if (cfg.strategyId !== "none") {
          const res = updateDroneMissions({
            engine: sim,
            stations,
            missions,
            nowMs: t,
            structureValue: sim.structureValue,
            weather,
            strategyId: cfg.strategyId,
          });
          missions = res.missions;
          for (const d of res.drops) {
            sim.applyTool("RETARDANT_DROP", d.row, d.col, weather);
            dropsUsed++;
          }
        }

        sim.step(weather, 1);
        lossCurve[s + 1] +=
          sim.stats().burntInfrastructure - baseStats.burntInfrastructure;
      }

      const st = sim.stats();
      agg.infraLost += st.burntInfrastructure - baseStats.burntInfrastructure;
      agg.valueLost += st.valueLost - baseStats.valueLost;
      agg.burntTiles += st.totalBurntTiles - baseStats.totalBurntTiles;
      agg.drops += dropsUsed;
      if (rep === 0) finalState = sim.state.map((row) => Int8Array.from(row));

      doneRuns++;
      onProgress?.(doneRuns / totalRuns, cfg);
      await yieldToUI();
    }

    results.push({
      ...cfg,
      strategyName: strategy?.name || cfg.strategyId,
      stationCount: stations.length,
      infraLost: agg.infraLost / replicates,
      valueLost: agg.valueLost / replicates,
      burntTiles: agg.burntTiles / replicates,
      drops: agg.drops / replicates,
      lossCurve: Array.from(lossCurve, (v) => v / replicates),
      finalState,
    });
  }

  // Rank + annotate savings vs. the worst performer (and vs. baseline if run)
  const baseline = results.find((r) => r.strategyId === "none");
  const worstLoss = Math.max(...results.map((r) => r.infraLost), 0.001);
  for (const r of results) {
    r.savedVsBaseline = baseline ? baseline.infraLost - r.infraLost : null;
    r.relativeLoss = r.infraLost / worstLoss;
  }
  results.sort((a, b) => a.infraLost - b.infraLost);
  return {
    results,
    horizonHours,
    replicates,
    startMs,
    initialInfra: baseStats.current,
  };
};
